/**
 * Local Bridge API client: create user/company/session, list sessions, questions, calculate.
 * All calls are gated on isLocalBridgeApiEnabled(); when disabled, methods throw or return null.
 */
import { localBridgeApiClient } from './localBridgeApiClient';
import { isLocalBridgeApiEnabled } from './bridgeSessionDataAdapter';
import { getApiErrorMessage } from '../utils/apiErrors';
import type { AssumptionEntry, MarketFitScoredOutput } from '../types/marketfitGuided';

export interface LocalUser {
  id: string;
  name: string;
  email?: string;
  created_at: string;
}

export interface LocalCompany {
  id: string;
  name: string;
  created_at: string;
  control_bridge_session_id?: string | null;
}

export interface LocalBridgeSession {
  id: string;
  company_id: string;
  user_id: string;
  status: string;
  current_revenue: number;
  current_expense: number;
  current_valuation?: number;
  target_valuation?: number;
  target_months?: number;
  bridge_objective?: string;
  session_analysis?: string;
  commitment_toll_cost_1hr?: number;
  domain?: string;
  created_at: string;
  updated_at: string;
  // Workshop (Business Objective Workshop flow)
  person_dependency_score?: number;
  revenue_predictability_score?: number;
  client_concentration_risk?: string;
  concern_12_months?: string;
  concern_sentiment?: string;
  replication_confidence_score?: number;
  what_breaks_first?: string;
  primary_constraint?: string;
  current_net_margin?: number;
  milestone_revenue?: number;
  implied_monthly_value?: number;
  implied_daily_value?: number;
  implied_hourly_value?: number;
  // Branching fields
  future_stagnation_concern?: string;
  orientation_path?: string;
  mentioned_constraints?: string;
  cash_flow_pressure_type?: string;
  sales_predictability_score?: number;
  scaling_breakpoint?: string;
  net_margin_percent?: number;
  milestone_value?: number;
  milestone_timeframe_months?: number;
  fragility_detected?: number;
  key_person_risk?: string;
  replication_risk?: string;
  replication_breakpoint?: string;
  considering_expansion?: number;
  workshop_state?: string;
  objection_notes?: string;
  lead_conversion_confidence?: number;
  forecasting_reliability?: number;
  process_documentation?: number;
  decision_bottleneck?: string;
  product_reliability?: number;
  differentiation_clarity?: number;
}

export interface NextQuestionResponse {
  question_id: string;
  domain: string;
  factor: string;
  text: string;
  options: Array<{ score: number; label: string }>;
  current_answer?: { score: number };
}

/** Full question with optional current answer (from GET .../questions). */
export interface QuestionWithAnswer {
  question_id: string;
  domain: string;
  factor: string;
  topic?: string;
  text: string;
  options: Array<{ score: number; label: string }>;
  current_answer: { score: number } | null;
}

function requireClient() {
  if (!isLocalBridgeApiEnabled() || !localBridgeApiClient) {
    throw new Error('Local Bridge API is not configured');
  }
  return localBridgeApiClient;
}

export async function listUsers(): Promise<LocalUser[]> {
  try {
    const client = requireClient();
    const res = await client.get<LocalUser[]>('/users');
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function createUser(
  name: string,
  email?: string,
  password?: string
): Promise<LocalUser | null> {
  try {
    const client = requireClient();
    const res = await client.post<LocalUser>('/users', {
      name,
      email: email || null,
      password: password || null,
    });
    return res.data;
  } catch {
    return null;
  }
}

export async function updateUser(
  userId: string,
  payload: { name?: string; email?: string }
): Promise<LocalUser | null> {
  try {
    const client = requireClient();
    const res = await client.patch<LocalUser>(`/users/${userId}`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function listCompanies(): Promise<LocalCompany[]> {
  try {
    const client = requireClient();
    const res = await client.get<LocalCompany[]>('/companies');
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function createCompany(name: string): Promise<LocalCompany | null> {
  try {
    const client = requireClient();
    const res = await client.post<LocalCompany>('/companies', { name });
    return res.data;
  } catch {
    return null;
  }
}

export async function getCompany(companyId: string): Promise<LocalCompany | null> {
  try {
    const client = requireClient();
    const res = await client.get<LocalCompany>(`/companies/${companyId}`);
    return res.data;
  } catch {
    return null;
  }
}

// ----- Company transactions (revenue, expense, sales) -----

export interface CompanyTransactionItem {
  id: string;
  company_id: string;
  transaction_date: string;
  amount: number;
  data_type: 'actual' | 'projected';
  transaction_type: 'revenue' | 'expense';
  product_id?: string | null;
  expense_category?: string | null;
  expense_type?: string | null;
  source_sale_id?: string | null;
  source_session_id?: string | null;
  created_at: string;
}

export interface CompanySaleItem {
  id: string;
  company_id: string;
  transaction_date: string;
  product_id?: string | null;
  product_name?: string | null;
  quantity: number;
  unit_price: number;
  total_revenue: number;
  cac_per_unit: number;
  total_cac: number;
  cogs_per_unit: number;
  total_cogs: number;
  data_type: 'actual' | 'projected';
  created_at: string;
}

export interface CompanySalesSummary {
  items: Array<{ product_session_id: string; product_name: string; amount: number }>;
  total: number;
}

export async function listCompanyTransactions(
  companyId: string,
  params?: { transaction_type?: 'revenue' | 'expense'; data_type?: 'actual' | 'projected'; period_id?: string }
): Promise<CompanyTransactionItem[]> {
  try {
    const client = requireClient();
    const res = await client.get<CompanyTransactionItem[]>(`/companies/${companyId}/transactions`, {
      params: params ?? {},
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function createCompanyTransaction(
  companyId: string,
  payload: {
    transaction_type: 'revenue' | 'expense';
    transaction_date: string;
    amount: number;
    product_id?: string | null;
    expense_category?: string | null;
    expense_type?: string | null;
    data_type?: 'actual' | 'projected';
  }
): Promise<CompanyTransactionItem | null> {
  try {
    const client = requireClient();
    const res = await client.post<CompanyTransactionItem>(`/companies/${companyId}/transactions`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function updateCompanyTransaction(
  companyId: string,
  itemId: string,
  payload: { amount?: number; transaction_date?: string; product_id?: string | null; expense_category?: string | null; expense_type?: string | null; data_type?: 'actual' | 'projected' }
): Promise<CompanyTransactionItem | null> {
  try {
    const client = requireClient();
    const res = await client.patch<CompanyTransactionItem>(
      `/companies/${companyId}/transactions/${itemId}`,
      payload
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function deleteCompanyTransaction(
  companyId: string,
  itemId: string
): Promise<boolean> {
  try {
    const client = requireClient();
    await client.delete(`/companies/${companyId}/transactions/${itemId}`);
    return true;
  } catch {
    return false;
  }
}

export async function listCompanySales(
  companyId: string,
  params?: { period_id?: string }
): Promise<CompanySaleItem[]> {
  try {
    const client = requireClient();
    const res = await client.get<CompanySaleItem[]>(`/companies/${companyId}/transactions/sales`, {
      params: params ?? {},
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function createCompanySale(
  companyId: string,
  payload: {
    transaction_date: string;
    product_id?: string | null;
    product_name?: string | null;
    quantity: number;
    unit_price: number;
    cac_per_unit?: number;
    cogs_per_unit?: number;
    data_type?: 'actual' | 'projected';
  }
): Promise<{ ok: boolean; sale_id: string } | null> {
  try {
    const client = requireClient();
    const res = await client.post<{ ok: boolean; sale_id: string }>(`/companies/${companyId}/transactions/sales`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function deleteCompanySale(companyId: string, saleId: string): Promise<boolean> {
  try {
    const client = requireClient();
    await client.delete(`/companies/${companyId}/transactions/sales/${saleId}`);
    return true;
  } catch {
    return false;
  }
}

export async function getCompanySalesSummary(companyId: string): Promise<CompanySalesSummary> {
  try {
    const client = requireClient();
    const res = await client.get<CompanySalesSummary>(`/companies/${companyId}/transactions/sales-summary`);
    return res.data ?? { items: [], total: 0 };
  } catch {
    return { items: [], total: 0 };
  }
}

export async function updateCompany(
  companyId: string,
  payload: { name?: string; control_bridge_session_id?: string | null }
): Promise<LocalCompany | null> {
  try {
    const client = requireClient();
    const res = await client.patch<LocalCompany>(`/companies/${companyId}`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export interface CreateBridgeSessionPayload {
  user_id: string;
  company_id: string;
  current_revenue: number;
  current_expense: number;
  current_valuation?: number;
  target_valuation?: number;
  target_months?: number;
  bridge_objective?: string;
  commitment_toll_cost_1hr?: number;
  domain?: string;
}

export async function createBridgeSession(
  payload: CreateBridgeSessionPayload
): Promise<LocalBridgeSession | null> {
  try {
    const client = requireClient();
    const res = await client.post<LocalBridgeSession>('/bridge-sessions', payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function listBridgeSessions(companyId?: string): Promise<LocalBridgeSession[]> {
  try {
    const client = requireClient();
    const params = companyId ? { company_id: companyId } : {};
    const res = await client.get<LocalBridgeSession[]>('/bridge-sessions', { params });
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function getBridgeSession(sessionId: string): Promise<LocalBridgeSession | null> {
  try {
    const client = requireClient();
    const res = await client.get<LocalBridgeSession>(`/bridge-sessions/${sessionId}`);
    return res.data;
  } catch {
    return null;
  }
}

export interface UpdateBridgeSessionPayload {
  status?: string;
  current_revenue?: number;
  current_expense?: number;
  current_valuation?: number;
  target_valuation?: number;
  target_months?: number;
  bridge_objective?: string;
  session_analysis?: string;
  commitment_toll_cost_1hr?: number;
  domain?: string;
}

export async function updateBridgeSession(
  sessionId: string,
  payload: UpdateBridgeSessionPayload
): Promise<LocalBridgeSession | null> {
  try {
    const client = requireClient();
    const res = await client.patch<LocalBridgeSession>(`/bridge-sessions/${sessionId}`, payload);
    return res.data;
  } catch {
    return null;
  }
}

/** Get full list of questions with current answers for the session (for navigation and progress). */
export async function getQuestions(
  sessionId: string,
  phase: 'current' | 'future' = 'current',
  domain?: string
): Promise<QuestionWithAnswer[]> {
  try {
    const client = requireClient();
    const params: { phase: string; domain?: string } = { phase };
    if (domain) params.domain = domain;
    const res = await client.get<QuestionWithAnswer[]>(
      `/bridge-sessions/${sessionId}/questions`,
      { params }
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function getNextQuestion(
  sessionId: string,
  phase: 'current' | 'future' = 'current'
): Promise<NextQuestionResponse | null> {
  try {
    const client = requireClient();
    const res = await client.get<NextQuestionResponse>(
      `/bridge-sessions/${sessionId}/next-question`,
      { params: { phase } }
    );
    return res.data;
  } catch {
    return null;
  }
}

export type SubmitAnswersResult =
  | { ok: true }
  | { ok: false; error: string };

export async function submitAnswers(
  sessionId: string,
  phase: 'current' | 'future',
  answers: Array<{ question_id: string; score: number }>
): Promise<SubmitAnswersResult> {
  try {
    const client = requireClient();
    await client.post(`/bridge-sessions/${sessionId}/answers`, { phase, answers });
    return { ok: true };
  } catch (e: unknown) {
    const error = getApiErrorMessage(e, 'Failed to save answer');
    if (process.env.NODE_ENV === 'development') {
      console.warn('[submitAnswers]', sessionId, error, e);
    }
    return { ok: false, error };
  }
}

export type CalculateSessionResult =
  | { ok: true; data: unknown }
  | { ok: false; error: string };

export async function calculateSession(sessionId: string): Promise<CalculateSessionResult> {
  try {
    const client = requireClient();
    const res = await client.post(`/bridge-sessions/${sessionId}/calculate`);
    return { ok: true, data: res.data };
  } catch (e: unknown) {
    return { ok: false, error: getApiErrorMessage(e, 'Calculation failed') };
  }
}

export interface FrictionItemWithQuestion {
  question_id: string;
  score: number;
  base_friction: number;
  adjusted_friction: number;
  text: string;
  domain: string;
  factor: string;
}

export async function getTopFriction(
  sessionId: string,
  limit: number = 10
): Promise<FrictionItemWithQuestion[]> {
  try {
    const client = requireClient();
    const res = await client.get<FrictionItemWithQuestion[]>(
      `/bridge-sessions/${sessionId}/friction`,
      { params: { limit } }
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function setBridgeCard(
  sessionId: string,
  items: Array<{ question_id: string; reason?: string; priority?: number }>
): Promise<boolean> {
  try {
    const client = requireClient();
    await client.post(`/bridge-sessions/${sessionId}/bridge-card`, { items });
    return true;
  } catch {
    return false;
  }
}

/** Bridge card item with optional Define Conditions fields */
export interface BridgeCardItemWithDetails {
  id: string;
  session_id: string;
  question_id: string;
  reason?: string;
  priority: number;
  changeable: boolean;
  created_at: string;
  future_condition?: string | null;
  future_answer_score?: number | null;
  assumption_text?: string | null;
  plank_description?: string | null;
  resource_assignment?: string | null;
  estimated_duration?: string | null;
  resource_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  duration?: string | null;
  remaining_duration?: string | null;
}

export interface CompanyEmployee {
  id: string;
  company_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export async function listCompanyEmployees(companyId: string): Promise<CompanyEmployee[]> {
  try {
    const client = requireClient();
    const res = await client.get<CompanyEmployee[]>(`/companies/${companyId}/employees`);
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function createCompanyEmployee(
  companyId: string,
  name: string
): Promise<CompanyEmployee | null> {
  try {
    const client = requireClient();
    const res = await client.post<CompanyEmployee>(`/companies/${companyId}/employees`, { name });
    return res.data;
  } catch {
    return null;
  }
}

export async function getBridgeCard(
  sessionId: string
): Promise<BridgeCardItemWithDetails[]> {
  try {
    const client = requireClient();
    const res = await client.get<BridgeCardItemWithDetails[]>(
      `/bridge-sessions/${sessionId}/bridge-card`
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export interface BridgeCardItemDetailsInput {
  question_id: string;
  future_condition?: string | null;
  future_answer_score?: number | null;
  assumption_text?: string | null;
  plank_description?: string | null;
  resource_assignment?: string | null;
  estimated_duration?: string | null;
  resource_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  duration?: string | null;
  remaining_duration?: string | null;
}

export async function updateBridgeCardDetails(
  sessionId: string,
  items: BridgeCardItemDetailsInput[]
): Promise<boolean> {
  try {
    const client = requireClient();
    await client.patch(`/bridge-sessions/${sessionId}/bridge-card/details`, {
      items,
    });
    return true;
  } catch (err) {
    console.error('[updateBridgeCardDetails] PATCH failed:', err);
    return false;
  }
}

/**
 * Fetch questions + answers export JSON and trigger browser download.
 * Returns true if download was triggered, false on error.
 */
export async function downloadQuestionsAnswersExport(sessionId: string): Promise<boolean> {
  try {
    const client = requireClient();
    const res = await client.get<Record<string, unknown>>(
      `/bridge-sessions/${sessionId}/export/questions-answers`,
      { responseType: 'json' }
    );
    const blob = new Blob([JSON.stringify(res.data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `session-${sessionId}-questions-answers.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  } catch {
    return false;
  }
}

// =============================================================================
// MarketFit (local bridge domain: products, situations, deals)
// =============================================================================

export interface MarketFitProduct {
  id: string;
  company_id: string;
  created_by_user_id?: string | null;
  name: string;
  description?: string | null;
  product_price?: number | null;
  product_cost_cogs?: number | null;
  product_cost_development?: number | null;
  inputs_benefits_json?: string | null;
  created_at: string;
  updated_at: string;
}

export interface MarketFitSituation {
  id: string;
  company_id: string;
  created_by_user_id?: string | null;
  name: string;
  situation_name?: string | null;
  description?: string | null;
  market_segment?: string | null;
  customer_type?: string | null;
  value_generated?: number | null;
  resource_cost?: number | null;
  details_json?: string | null;
  created_at: string;
  updated_at: string;
}

export interface MarketFitDeal {
  id: string;
  company_id: string;
  created_by_user_id?: string | null;
  bridge_session_id?: string | null;
  name: string;
  description?: string | null;
  state: string;
  actor_id?: string | null;
  connection_links_json?: string | null;
  actor_situation_alignment?: number | null;
  deal_score?: number | null;
  product_situation_alignment?: number | null;
  product_situation_value?: number | null;
  pair_scores_json?: string | null;
  product_ids: string[];
  situation_ids: string[];
  created_at: string;
  updated_at: string;
}

export async function listMarketFitProducts(companyId: string): Promise<MarketFitProduct[]> {
  try {
    const client = requireClient();
    const res = await client.get<MarketFitProduct[]>('/marketfit/products', {
      params: { company_id: companyId },
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function createMarketFitProduct(payload: {
  company_id: string;
  created_by_user_id?: string | null;
  name: string;
  description?: string | null;
  product_price?: number | null;
  product_cost_cogs?: number | null;
  product_cost_development?: number | null;
  inputs_benefits_json?: string | null;
}): Promise<MarketFitProduct | null> {
  try {
    const client = requireClient();
    const res = await client.post<MarketFitProduct>('/marketfit/products', payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function getMarketFitProduct(productId: string): Promise<MarketFitProduct | null> {
  try {
    const client = requireClient();
    const res = await client.get<MarketFitProduct>(`/marketfit/products/${productId}`);
    return res.data;
  } catch {
    return null;
  }
}

export async function updateMarketFitProduct(
  productId: string,
  payload: {
    name?: string;
    description?: string | null;
    product_price?: number | null;
    product_cost_cogs?: number | null;
    product_cost_development?: number | null;
    inputs_benefits_json?: string | null;
  }
): Promise<MarketFitProduct | null> {
  try {
    const client = requireClient();
    const res = await client.patch<MarketFitProduct>(`/marketfit/products/${productId}`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function listMarketFitSituations(companyId: string): Promise<MarketFitSituation[]> {
  try {
    const client = requireClient();
    const res = await client.get<MarketFitSituation[]>('/marketfit/situations', {
      params: { company_id: companyId },
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function createMarketFitSituation(payload: {
  company_id: string;
  created_by_user_id?: string | null;
  name: string;
  situation_name?: string | null;
  description?: string | null;
  market_segment?: string | null;
  customer_type?: string | null;
  value_generated?: number | null;
  resource_cost?: number | null;
  details_json?: string | null;
}): Promise<MarketFitSituation | null> {
  try {
    const client = requireClient();
    const res = await client.post<MarketFitSituation>('/marketfit/situations', payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function getMarketFitSituation(situationId: string): Promise<MarketFitSituation | null> {
  try {
    const client = requireClient();
    const res = await client.get<MarketFitSituation>(`/marketfit/situations/${situationId}`);
    return res.data;
  } catch {
    return null;
  }
}

export async function updateMarketFitSituation(
  situationId: string,
  payload: {
    name?: string;
    situation_name?: string | null;
    description?: string | null;
    market_segment?: string | null;
    customer_type?: string | null;
    value_generated?: number | null;
    resource_cost?: number | null;
    details_json?: string | null;
  }
): Promise<MarketFitSituation | null> {
  try {
    const client = requireClient();
    const res = await client.patch<MarketFitSituation>(`/marketfit/situations/${situationId}`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function listMarketFitDeals(
  companyId: string,
  bridgeSessionId?: string | null
): Promise<MarketFitDeal[]> {
  try {
    const client = requireClient();
    const params: { company_id: string; bridge_session_id?: string } = { company_id: companyId };
    if (bridgeSessionId) params.bridge_session_id = bridgeSessionId;
    const res = await client.get<MarketFitDeal[]>('/marketfit/deals', { params });
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function createMarketFitDeal(payload: {
  company_id: string;
  created_by_user_id?: string | null;
  bridge_session_id?: string | null;
  name: string;
  description?: string | null;
  product_ids: string[];
  situation_ids: string[];
  actor_id?: string | null;
  connection_links_json?: string | null;
  actor_situation_alignment?: number | null;
}): Promise<MarketFitDeal | null> {
  try {
    const client = requireClient();
    const res = await client.post<MarketFitDeal>('/marketfit/deals', payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function getMarketFitDeal(dealId: string): Promise<MarketFitDeal | null> {
  try {
    const client = requireClient();
    const res = await client.get<MarketFitDeal>(`/marketfit/deals/${dealId}`);
    return res.data;
  } catch {
    return null;
  }
}

export interface MarketFitEmotionalDelta {
  id: string;
  actor_id: string;
  deal_id?: string | null;
  source: string;
  deltas_json: string;
  created_at: string;
}

export interface MarketFitEmotionalJustification {
  deal_id: string;
  actor_id: string;
  emotional_value_score: number;
  emotional_cost_score: number;
  identity_alignment_score: number;
  justification_pass: boolean;
  created_at: string;
  updated_at: string;
}

export interface MarketFitCommitmentDecision {
  deal_id: string;
  actor_id: string;
  situation_id: string;
  product_id: string;
  authorized_amount: number;
  timing: string;
  reversibility: number;
  decision_pass: boolean;
  created_at: string;
  updated_at: string;
}

export interface MarketFitSituationDelta {
  deal_id: string;
  situation_id: string;
  deltas_json: string;
  created_at: string;
  updated_at: string;
}

export interface MarketFitDealDetail extends MarketFitDeal {
  products: MarketFitProduct[];
  situations: MarketFitSituation[];
  actor?: MarketFitActor | null;
  emotional_justifications?: MarketFitEmotionalJustification[];
  commitment_decisions?: MarketFitCommitmentDecision[];
  situation_deltas?: MarketFitSituationDelta[];
  emotional_deltas?: MarketFitEmotionalDelta[];
}

export interface MarketFitActor {
  id: string;
  company_id: string;
  created_by_user_id?: string | null;
  actor_name?: string | null;
  actor_role?: string | null;
  details_json?: string | null;
  created_at: string;
  updated_at: string;
}

export interface MarketFitScores {
  svp?: number | null;
  acr?: number | null;
  ce?: number | null;
  marketfit_score?: number | null;
  interpretation?: { band: string; label: string } | null;
}

export async function getMarketFitDealScores(dealId: string): Promise<MarketFitScores | null> {
  try {
    const client = requireClient();
    const res = await client.get<MarketFitScores>(`/marketfit/deals/${dealId}/scores`);
    return res.data;
  } catch {
    return null;
  }
}

export async function getMarketFitDealDetail(dealId: string): Promise<MarketFitDealDetail | null> {
  try {
    const client = requireClient();
    const res = await client.get<MarketFitDealDetail>(`/marketfit/deals/${dealId}/detail`);
    return res.data;
  } catch {
    return null;
  }
}

export async function updateMarketFitDeal(
  dealId: string,
  payload: {
    name?: string;
    description?: string | null;
    state?: string;
    bridge_session_id?: string | null;
    product_ids?: string[];
    situation_ids?: string[];
    actor_id?: string | null;
    connection_links_json?: string | null;
    actor_situation_alignment?: number | null;
  }
): Promise<MarketFitDeal | null> {
  try {
    const client = requireClient();
    const res = await client.patch<MarketFitDeal>(`/marketfit/deals/${dealId}`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function listMarketFitActors(companyId: string): Promise<MarketFitActor[]> {
  try {
    const client = requireClient();
    const res = await client.get<MarketFitActor[]>('/marketfit/actors', {
      params: { company_id: companyId },
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function createMarketFitActor(payload: {
  company_id: string;
  created_by_user_id?: string | null;
  actor_name?: string | null;
  actor_role?: string | null;
  details_json?: string | null;
}): Promise<MarketFitActor | null> {
  try {
    const client = requireClient();
    const res = await client.post<MarketFitActor>('/marketfit/actors', payload);
    return res.data;
  } catch {
    return null;
  }
}

// ----- MarketFit core schema v2 (value conversion) -----

export async function listEmotionalDeltas(params: {
  deal_id?: string;
  actor_id?: string;
}): Promise<MarketFitEmotionalDelta[]> {
  try {
    const client = requireClient();
    const res = await client.get<MarketFitEmotionalDelta[]>('/marketfit/emotional-deltas', { params });
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function createEmotionalDelta(payload: {
  actor_id: string;
  deal_id?: string | null;
  source: string;
  deltas_json: string;
}): Promise<MarketFitEmotionalDelta | null> {
  try {
    const client = requireClient();
    const res = await client.post<MarketFitEmotionalDelta>('/marketfit/emotional-deltas', payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function deleteEmotionalDelta(deltaId: string): Promise<boolean> {
  try {
    const client = requireClient();
    await client.delete(`/marketfit/emotional-deltas/${deltaId}`);
    return true;
  } catch {
    return false;
  }
}

export async function listEmotionalJustifications(dealId: string): Promise<MarketFitEmotionalJustification[]> {
  try {
    const client = requireClient();
    const res = await client.get<MarketFitEmotionalJustification[]>(`/marketfit/deals/${dealId}/emotional-justifications`);
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function upsertEmotionalJustification(
  dealId: string,
  payload: {
    deal_id: string;
    actor_id: string;
    emotional_value_score: number;
    emotional_cost_score: number;
    identity_alignment_score: number;
    justification_pass: boolean;
  }
): Promise<MarketFitEmotionalJustification | null> {
  try {
    const client = requireClient();
    const res = await client.put<MarketFitEmotionalJustification>(
      `/marketfit/deals/${dealId}/emotional-justifications`,
      { ...payload, deal_id: dealId }
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function listCommitmentDecisions(dealId: string): Promise<MarketFitCommitmentDecision[]> {
  try {
    const client = requireClient();
    const res = await client.get<MarketFitCommitmentDecision[]>(`/marketfit/deals/${dealId}/commitment-decisions`);
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function upsertCommitmentDecision(
  dealId: string,
  payload: {
    deal_id: string;
    actor_id: string;
    situation_id: string;
    product_id: string;
    authorized_amount: number;
    timing: string;
    reversibility: number;
    decision_pass: boolean;
  }
): Promise<MarketFitCommitmentDecision | null> {
  try {
    const client = requireClient();
    const res = await client.put<MarketFitCommitmentDecision>(
      `/marketfit/deals/${dealId}/commitment-decisions`,
      { ...payload, deal_id: dealId }
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function listSituationDeltas(dealId: string): Promise<MarketFitSituationDelta[]> {
  try {
    const client = requireClient();
    const res = await client.get<MarketFitSituationDelta[]>(`/marketfit/deals/${dealId}/situation-deltas`);
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function upsertSituationDelta(
  dealId: string,
  payload: { deal_id: string; situation_id: string; deltas_json: string }
): Promise<MarketFitSituationDelta | null> {
  try {
    const client = requireClient();
    const res = await client.put<MarketFitSituationDelta>(`/marketfit/deals/${dealId}/situation-deltas`, {
      ...payload,
      deal_id: dealId,
    });
    return res.data;
  } catch {
    return null;
  }
}

// ----- MarketFit Guided Flow (state machine) -----
export interface GuidedFlowStateConfig {
  id: string;
  title: string;
  user_prompt: string;
  expected_output: string | null;
  guards: string[];
  allowed_failure_flags: string[];
  next_state_id: string | null;
  loop_back_states: string[] | null;
}

export interface GuidedSessionState {
  marketfit_intent?: { type: string; confidence_bias?: string } | null;
  assumptions?: AssumptionEntry[] | null;
  marketfit_scored_output?: MarketFitScoredOutput | null;
  product_belief?: Record<string, unknown> | null;
  actors?: Record<string, unknown>[] | null;
  situation_boundary?: Record<string, unknown> | null;
  situation_value?: Record<string, unknown> | null;
  actor_emotional_baseline?: Record<string, unknown> | null;
  functional_interventions?: Record<string, unknown>[] | null;
  asset_interactions?: Record<string, unknown>[] | null;
  emotional_delta?: Record<string, unknown> | null;
  emotional_justification?: Record<string, unknown> | null;
  commitment_decision?: Record<string, unknown> | null;
  deal_environment?: Record<string, unknown> | null;
}

export interface DealEnvironment {
  multi_actor: boolean;
  spatial_mismatch: boolean;
  temporal_volatility: 'low' | 'medium' | 'high';
  supply_constraint: 'none' | 'local_low_substitutability' | 'regional_low_substitutability';
  dominant_risk: 'price_sensitivity' | 'coordination_failure' | 'capacity_shortage' | 'adoption_resistance';
}

export interface GuidedFlowScopeResponse {
  complete: boolean;
  deal_environment?: DealEnvironment | null;
  missing_situation: string[];
  missing_actors: Array<{ actor_index: number; missing: string[] }>;
}

export interface GuidedFlowExtractResponse {
  extracted: Record<string, unknown>;
  next_state_id: string | null;
  gate_result: 'proceed' | 'clarify' | 'flag';
  failure_flag: string | null;
  clarification_message: string | null;
}

export interface GuidedFlowImportResponse {
  deal_id: string;
  product_id: string;
  situation_id: string;
  actor_id: string;
}

export async function getGuidedFlowConfig(): Promise<GuidedFlowStateConfig[]> {
  try {
    const client = requireClient();
    const res = await client.get<GuidedFlowStateConfig[]>('/marketfit/guided-flow/config');
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function postGuidedFlowExtract(
  stateId: string,
  userMessage: string,
  sessionState: GuidedSessionState | null
): Promise<GuidedFlowExtractResponse | null> {
  try {
    const client = requireClient();
    const res = await client.post<GuidedFlowExtractResponse>('/marketfit/guided-flow/extract', {
      state_id: stateId,
      user_message: userMessage,
      session_state: sessionState,
    });
    return res.data;
  } catch {
    return null;
  }
}

export async function postGuidedFlowScope(
  sessionState: GuidedSessionState | null
): Promise<GuidedFlowScopeResponse | null> {
  try {
    const client = requireClient();
    const res = await client.post<GuidedFlowScopeResponse>('/marketfit/guided-flow/scope', {
      session_state: sessionState,
    });
    return res.data;
  } catch {
    return null;
  }
}

export async function postGuidedFlowImport(
  sessionId: string,
  companyId: string,
  state: GuidedSessionState
): Promise<GuidedFlowImportResponse | null> {
  try {
    const client = requireClient();
    const res = await client.post<GuidedFlowImportResponse>('/marketfit/guided-flow/import', {
      session_id: sessionId,
      company_id: companyId,
      state,
    });
    return res.data;
  } catch {
    return null;
  }
}

export interface GuidedFlowScoreResponse {
  svp: number;
  acr: number;
  ce: number;
  marketfit_score: number;
  interpretation: { band: string; label: string };
  confidence_weight: number;
  weighted_marketfit_score: number;
}

export async function postGuidedFlowScore(
  sessionState: GuidedSessionState | null
): Promise<GuidedFlowScoreResponse | null> {
  try {
    const client = requireClient();
    const res = await client.post<GuidedFlowScoreResponse>('/marketfit/guided-flow/score', {
      session_state: sessionState,
    });
    return res.data;
  } catch {
    return null;
  }
}

export interface GuidedFlowGenerateIcpResponse {
  icp_summary: Record<string, unknown> | null;
  reason?: string | null;
}

export async function postGuidedFlowGenerateIcp(
  sessionState: GuidedSessionState | null
): Promise<GuidedFlowGenerateIcpResponse | null> {
  try {
    const client = requireClient();
    const res = await client.post<GuidedFlowGenerateIcpResponse>('/marketfit/guided-flow/generate-icp', {
      session_state: sessionState,
    });
    return res.data;
  } catch {
    return null;
  }
}

// ----- Business Objective Workshop (artifact-based 10-screen flow) -----

export interface WorkshopObjective {
  id: string;
  bridge_session_id: string;
  company_id: string;
  business_id?: string | null;
  state: string;
  screen_index: number;
  decision_authority_present?: boolean | null;
  timeline_shortening_importance?: string | null;
  objective_description?: string | null;
  objective_domain?: string | null;
  target_date?: string | null;
  target_metric_type?: string | null;
  target_metric?: number | null;
  estimated_months_to_goal?: number | null;
  confidence_score?: number | null;
  emotional_importance_score?: number | null;
  emotional_why?: string | null;
  potential_months_saved?: number | null;
  financial_delta?: number | null;
  value_per_month?: number | null;
  estimated_acceleration_value?: number | null;
  commitment_toll?: number | null;
  toll_status?: string | null;
  session_outcome?: string | null;
  current_period_id?: string | null;
  target_period_id?: string | null;
  vision_id?: string | null;
  workshop_participants?: string | null;
  workshop_datetime?: string | null;
  primary_route?: string | null;
  routing_reason?: string | null;
  dominant_delay_category?: string | null;
  valuation_multiplier?: number | null;
  routing_version?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkshopRouteResponse {
  primary_route: string;
  routing_reason: string;
  dominant_delay_category?: string | null;
  valuation_multiplier?: number | null;
  routing_version?: string | null;
}

export interface WorkshopPeriod {
  id: string;
  business_id: string;
  period_label?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  revenue?: number | null;
  expense?: number | null;
  valuation?: number | null;
  net_income?: number | null;
}

export interface WorkshopVision {
  id: string;
  objective_id: string;
  vision_description?: string | null;
  time_horizon_months?: number | null;
}

export interface WorkshopDelay {
  id: string;
  vision_id: string;
  objective_id: string;
  delay_category: string;
  description?: string | null;
  impact_score?: number | null;
  sequence_order?: number | null;
  state?: string | null;
}

export interface WorkshopPerson {
  id: string;
  objective_id: string;
  name: string;
  role?: string | null;
  persona_type?: string | null;
  decision_authority?: boolean | number | null;
  influence_level?: number | null;
}

export interface WorkshopDomainStrategy {
  id: string;
  objective_id: string;
  domain: string;
  strategies?: string[];
  launch_date?: string | null;
  target_exit_date?: string | null;
  current_vision?: string | null;
  emotional_impact?: string | null;
  target_revenue?: number | null;
  target_valuation?: number | null;
}

export interface WorkshopObjectiveResponse {
  objective: WorkshopObjective;
  current_period?: WorkshopPeriod | null;
  target_period?: WorkshopPeriod | null;
  vision?: WorkshopVision | null;
  delays: WorkshopDelay[];
  persons: WorkshopPerson[];
  domain_strategies?: WorkshopDomainStrategy[];
  company_name?: string | null;
  /** Set when commit-domain creates a Product Domain session */
  product_domain_session_id?: string | null;
}

/**
 * Backend GET /objective returns a flat objective-shaped JSON; advance returns the same after persisting.
 * Workshop UI expects `{ objective, delays, persons }` — wrap flat payloads so `data.objective.screen_index` works.
 */
export function normalizeWorkshopObjectiveResponse(data: unknown): WorkshopObjectiveResponse | null {
  if (data == null || typeof data !== 'object') {
    return null;
  }
  const d = data as Record<string, unknown>;
  if (d.objective && typeof d.objective === 'object') {
    return {
      objective: d.objective as WorkshopObjective,
      delays: Array.isArray(d.delays) ? (d.delays as WorkshopDelay[]) : [],
      persons: Array.isArray(d.persons) ? (d.persons as WorkshopPerson[]) : [],
      vision: (d.vision as WorkshopVision) ?? undefined,
      domain_strategies: d.domain_strategies as WorkshopDomainStrategy[] | undefined,
      company_name: (d.company_name as string) ?? null,
      product_domain_session_id: d.product_domain_session_id as string | undefined,
      current_period: (d.current_period as WorkshopPeriod) ?? null,
      target_period: (d.target_period as WorkshopPeriod) ?? null,
    };
  }
  if (
    'screen_index' in d ||
    'bridge_session_id' in d ||
    d.id !== undefined ||
    d.state !== undefined
  ) {
    return {
      objective: d as unknown as WorkshopObjective,
      delays: [],
      persons: [],
      company_name: (d.company_name as string) ?? null,
    };
  }
  return null;
}

export interface WorkshopNextScreenResponse {
  screen_index: number;
  screen_config: Record<string, unknown>;
  can_offer_toll: boolean;
  gate_failures: string[];
}

export interface WorkshopScreenConfig {
  id: string;
  title: string;
  prompt: string;
  fields: Array<{ key: string; label: string; type: string; required?: boolean; options?: Array<{ value: string; label: string }> }>;
}

const WORKSHOP_ARTIFACT_BASE = '/business-objective-workshop/sessions';

export interface DomainConfig {
  strategies: Record<string, Array<{ value: string; label: string }>>;
  fields: Record<string, Array<{ key: string; label: string; type: string }>>;
}

export async function getWorkshopDomainConfig(): Promise<DomainConfig | null> {
  try {
    const client = requireClient();
    const res = await client.get<DomainConfig>(`${WORKSHOP_ARTIFACT_BASE}/domain-config`);
    return res.data;
  } catch {
    return null;
  }
}

export async function getWorkshopObjective(sessionId: string): Promise<WorkshopObjectiveResponse | null> {
  try {
    const client = requireClient();
    const res = await client.get(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/objective`);
    return normalizeWorkshopObjectiveResponse(res.data);
  } catch {
    return null;
  }
}

export async function updateWorkshopObjective(
  sessionId: string,
  payload: Record<string, unknown>
): Promise<WorkshopObjectiveResponse | null> {
  try {
    const client = requireClient();
    const res = await client.patch(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/objective`, payload);
    return normalizeWorkshopObjectiveResponse(res.data);
  } catch {
    return null;
  }
}

export async function determineWorkshopRoute(
  sessionId: string
): Promise<WorkshopRouteResponse | null> {
  try {
    const client = requireClient();
    const res = await client.post<WorkshopRouteResponse>(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/determine-route`);
    return res.data;
  } catch {
    return null;
  }
}

export async function commitDomainFromControlBridge(
  sessionId: string,
  payload: { selected_bridge_domain: string; commitment_toll?: number; toll_status: string }
): Promise<WorkshopObjectiveResponse | null> {
  try {
    const client = requireClient();
    const res = await client.post(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/commit-domain`, payload);
    return normalizeWorkshopObjectiveResponse(res.data);
  } catch {
    return null;
  }
}

export async function createWorkshopBusinessPeriod(
  sessionId: string,
  payload: { role: 'current' | 'target'; period_label?: string; period_start?: string; period_end?: string; revenue?: number; expense?: number; valuation?: number; legal_name?: string }
): Promise<{ id: string; period_label?: string; revenue?: number; expense?: number; valuation?: number; net_income?: number } | null> {
  try {
    const client = requireClient();
    const res = await client.post(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/business-periods`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function updateWorkshopBusinessPeriod(
  sessionId: string,
  periodId: string,
  payload: { revenue?: number; expense?: number; valuation?: number; period_label?: string; period_start?: string; period_end?: string }
): Promise<{ id: string; updated: boolean } | null> {
  try {
    const client = requireClient();
    const res = await client.patch(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/business-periods/${periodId}`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function createWorkshopVision(
  sessionId: string,
  payload: { vision_description?: string; time_horizon_months?: number }
): Promise<{ id: string; vision_description?: string; time_horizon_months?: number } | null> {
  try {
    const client = requireClient();
    const res = await client.post(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/vision`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function updateWorkshopVision(
  sessionId: string,
  payload: { vision_description?: string; time_horizon_months?: number }
): Promise<{ id: string; updated: boolean } | null> {
  try {
    const client = requireClient();
    const res = await client.patch(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/vision`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function createWorkshopDelay(
  sessionId: string,
  payload: { delay_category: string; description?: string; impact_score?: number; sequence_order?: number }
): Promise<{ id: string; delay_category: string; description?: string; impact_score?: number } | null> {
  try {
    const client = requireClient();
    const res = await client.post(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/delays`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function updateWorkshopDelay(
  sessionId: string,
  delayId: string,
  payload: Record<string, unknown>
): Promise<{ id: string; updated: boolean } | null> {
  try {
    const client = requireClient();
    const res = await client.patch(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/delays/${delayId}`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function deleteWorkshopDelay(sessionId: string, delayId: string): Promise<boolean> {
  try {
    const client = requireClient();
    await client.delete(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/delays/${delayId}`);
    return true;
  } catch {
    return false;
  }
}

export async function createWorkshopPerson(
  sessionId: string,
  payload: { name: string; role: string; persona_type?: string; decision_authority?: boolean; influence_level?: number }
): Promise<{ id: string; name: string; role: string } | null> {
  try {
    const client = requireClient();
    const res = await client.post(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/persons`, payload);
    return res.data;
  } catch {
    return null;
  }
}

export async function advanceWorkshopScreen(
  sessionId: string,
  response: Record<string, unknown>
): Promise<WorkshopObjectiveResponse | null> {
  try {
    const client = requireClient();
    const res = await client.post(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/advance`, {
      response,
    });
    return normalizeWorkshopObjectiveResponse(res.data);
  } catch {
    return null;
  }
}

export async function getWorkshopNextScreen(sessionId: string): Promise<WorkshopNextScreenResponse | null> {
  try {
    const client = requireClient();
    const res = await client.get<WorkshopNextScreenResponse>(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/next-screen`);
    return res.data;
  } catch {
    return null;
  }
}

export async function evaluateWorkshopTollGate(sessionId: string): Promise<{ eligible: boolean; failures: string[] } | null> {
  try {
    const client = requireClient();
    const res = await client.post<{ eligible: boolean; failures: string[] }>(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/gate-check`);
    return res.data;
  } catch {
    return null;
  }
}

export async function getWorkshopObjectiveSummary(sessionId: string): Promise<WorkshopObjectiveResponse | null> {
  try {
    const client = requireClient();
    const res = await client.get(`${WORKSHOP_ARTIFACT_BASE}/${sessionId}/summary`);
    return normalizeWorkshopObjectiveResponse(res.data);
  } catch {
    return null;
  }
}

export const DELAY_CATEGORIES = [
  'DESTINATION_UNCLEAR',
  'DECISION_BOTTLENECK',
  'MULTITASKING',
  'WORK_OUT_OF_SEQUENCE',
  'UNNECESSARY_WORK',
  'HANDOFF_LATENCY',
  'SKILL_GAP',
  'CAPITAL_CONSTRAINT',
  'MARKET_UNCERTAINTY',
  'EXECUTION_FRICTION',
  'OTHER',
];

// ----- Business Objective Workshop (legacy 7-phase branching) -----

export interface WorkshopPromptConfig {
  id: string;
  user_prompt: string;
  input_type: 'free_text' | 'free_text_with_tag' | 'scale_1_10' | 'multichoice' | 'yes_no';
  expected_output: string;
  tag_options?: string[];
  scale_labels?: Record<string, string>;
  options?: Array<{ value: string; label: string }>;
}

export interface WorkshopPhaseConfig {
  id: string;
  title: string;
  subtitle?: string;
  prompts?: WorkshopPromptConfig[];
}

export interface AnalyzeOrientationResponse {
  path: 'stability_test' | 'constraint_deepening';
  mentioned_constraints: string[];
}

export interface WorkshopNextStepResponse {
  state_id: string;
  prompts: WorkshopPromptConfig[];
  can_skip_phase_4: boolean;
}

export interface FrictionSummaryResponse {
  strengths: string[];
  constraints: string[];
}

export async function getWorkshopConfig(): Promise<WorkshopPhaseConfig[]> {
  try {
    const client = requireClient();
    const res = await client.get<WorkshopPhaseConfig[]>(
      '/business-objective-workshop/config'
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export interface BridgeSessionWorkshopUpdatePayload {
  person_dependency_score?: number;
  revenue_predictability_score?: number;
  client_concentration_risk?: string;
  concern_12_months?: string;
  concern_sentiment?: string;
  replication_confidence_score?: number;
  what_breaks_first?: string;
  primary_constraint?: string;
  current_revenue?: number;
  current_expense?: number;
  current_valuation?: number;
  target_valuation?: number;
  target_months?: number;
  bridge_objective?: string;
  current_net_margin?: number;
  milestone_revenue?: number;
  commitment_toll_cost_1hr?: number;
  future_stagnation_concern?: string;
  orientation_path?: string;
  mentioned_constraints?: string;
  cash_flow_pressure_type?: string;
  sales_predictability_score?: number;
  scaling_breakpoint?: string;
  net_margin_percent?: number;
  milestone_value?: number;
  milestone_timeframe_months?: number;
  fragility_detected?: number;
  key_person_risk?: string;
  replication_risk?: string;
  replication_breakpoint?: string;
  considering_expansion?: number;
  workshop_state?: string;
  objection_notes?: string;
  lead_conversion_confidence?: number;
  forecasting_reliability?: number;
  process_documentation?: number;
  decision_bottleneck?: string;
  product_reliability?: number;
  differentiation_clarity?: number;
  clear_fields?: string[];
}

export async function updateBridgeSessionWorkshop(
  sessionId: string,
  payload: BridgeSessionWorkshopUpdatePayload
): Promise<LocalBridgeSession | null> {
  try {
    const client = requireClient();
    const res = await client.patch<LocalBridgeSession>(
      `/business-objective-workshop/${sessionId}`,
      payload
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function getSessionFrictionHighlights(
  sessionId: string
): Promise<string[]> {
  try {
    const client = requireClient();
    const res = await client.get<string[]>(
      `/business-objective-workshop/${sessionId}/friction-highlights`
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function analyzeOrientation(
  text: string
): Promise<AnalyzeOrientationResponse | null> {
  try {
    const client = requireClient();
    const res = await client.post<AnalyzeOrientationResponse>(
      '/business-objective-workshop/analyze-orientation',
      { text }
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function getWorkshopNextStep(
  sessionId: string
): Promise<WorkshopNextStepResponse | null> {
  try {
    const client = requireClient();
    const res = await client.get<WorkshopNextStepResponse>(
      `/business-objective-workshop/${sessionId}/next-step`
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function getSessionFrictionSummary(
  sessionId: string
): Promise<FrictionSummaryResponse | null> {
  try {
    const client = requireClient();
    const res = await client.get<FrictionSummaryResponse>(
      `/business-objective-workshop/${sessionId}/friction-summary`
    );
    return res.data;
  } catch {
    return null;
  }
}
